import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem as MenuItemComponent,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import UniversalBadge from '../common/UniversalBadge';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestoreIcon from '@mui/icons-material/Restore';
import { Organization } from '../../types';
import { useNotification } from '../../context/NotificationContext';
import { organizationService, userManagementService } from '../../services';
import InviteUserDialog from './InviteUserDialog';
import ServiceLimitsCard from './ServiceLimitsCard';
import QSConfigCard from './QSConfigCard';
import UseCasesConfigCard from './UseCasesConfigCard';

const OrganizationDetail: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  // Rolle-Bearbeitung entfernt - wird vom Backend verwaltet
  const [organizationMenuAnchor, setOrganizationMenuAnchor] = useState<null | HTMLElement>(null);
  // Loading-States für User-Aktionen
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [restoringUserId, setRestoringUserId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  useEffect(() => {
    const loadOrganization = async () => {
      setLoading(true);
      try {
        if (organizationId) {
          const orgData = await organizationService.getOrganization(organizationId);
          setOrganization(orgData);
        }
      } catch (error) {
        console.error('Error loading organization:', error);
        showNotification(t('admin:orgDetail.loadError'), 'error');
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      loadOrganization();
    }
  }, [organizationId, navigate, showNotification]);

  // Organization Actions
  const handleDeactivateOrganization = async () => {
    if (!organization || !organizationId) return;

    const confirmMessage = `Möchten Sie die Organisation "${organization.name}" deaktivieren?\n\nDie Organisation bleibt erhalten, wird aber als inaktiv markiert.`;

    if (window.confirm(confirmMessage)) {
      try {
        await organizationService.deactivateOrganization(organizationId);
        showNotification(t('admin:messages.deactivateSuccess'), 'success');
        setOrganization(prev => prev ? { ...prev, status: 'inactive' } : null);
      } catch (error) {
        console.error('Fehler beim Deaktivieren:', error);
        showNotification(t('admin:messages.deactivateError'), 'error');
      }
    }
    setOrganizationMenuAnchor(null);
  };

  const handleActivateOrganization = async () => {
    if (!organization || !organizationId) return;

    const confirmMessage = `Möchten Sie die Organisation "${organization.name}" wieder aktivieren?`;

    if (window.confirm(confirmMessage)) {
      try {
        await organizationService.activateOrganization(organizationId);
        showNotification(t('admin:messages.activateSuccess'), 'success');
        setOrganization(prev => prev ? { ...prev, status: 'active' } : null);
      } catch (error) {
        console.error('Fehler beim Aktivieren:', error);
        showNotification(t('admin:messages.activateError'), 'error');
      }
    }
    setOrganizationMenuAnchor(null);
  };

  const handleDeleteOrganization = async () => {
    if (!organization || !organizationId) return;

    const confirmMessage = `⚠️ ACHTUNG: Möchten Sie die Organisation "${organization.name}" ENDGÜLTIG löschen?\n\n` +
      `Diese Aktion kann NICHT rückgängig gemacht werden!\n` +
      `Alle Benutzer, Provider und Verträge werden permanent gelöscht.`;

    if (window.confirm(confirmMessage)) {
      const doubleConfirm = window.confirm(`Sind Sie sich WIRKLICH sicher?\n\nOrganisation "${organization.name}" wird PERMANENT gelöscht!`);

      if (doubleConfirm) {
        try {
          await organizationService.deleteOrganization(organizationId);
          showNotification(t('admin:messages.permanentlyDeleteSuccess'), 'success');
          // Zur Übersicht zurückkehren
          navigate('/admin');
        } catch (error) {
          console.error('Fehler beim Löschen:', error);
          showNotification(t('admin:messages.permanentlyDeleteError'), 'error');
        }
      }
    }
    setOrganizationMenuAnchor(null);
  };

  const handleInviteSuccess = async () => {
    // Cache invalidieren und Organisation neu laden
    if (organizationId) {
      try {
        const updatedOrg = await organizationService.getOrganization(organizationId);
        setOrganization(updatedOrg);
      } catch (error) {
        console.error('Fehler beim Neuladen der Organisation:', error);
      }
    }
  };

  const handleServiceLimitsUpdate = (updatedOrganization: Organization) => {
    setOrganization(updatedOrganization);
  };

  // Rolle-Bearbeitung Funktionen entfernt - wird vom Backend verwaltet

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!organization) return;

    const confirmMessage = t('admin:orgDetail.userRemoveConfirm', { name: userName });

    if (window.confirm(confirmMessage)) {
      try {
        setRemovingUserId(userId);
        await userManagementService.removeUserAppAccess(userId);
        showNotification(t('admin:orgDetail.userRemoveSuccess', { name: userName }), 'success');

        if (organizationId) {
          const updatedOrg = await organizationService.getOrganization(organizationId);
          setOrganization(updatedOrg);
        }
      } catch (error) {
        console.error('Error removing user:', error);
        showNotification(t('admin:orgDetail.userRemoveError'), 'error');
      } finally {
        setRemovingUserId(null);
      }
    }
  };

  const handleRestoreUserAppAccess = async (userId: string, userName: string) => {
    if (!organization) return;

    const confirmMessage = t('admin:orgDetail.userRestoreConfirm', { name: userName });

    if (window.confirm(confirmMessage)) {
      try {
        setRestoringUserId(userId);
        await userManagementService.restoreUserAppAccess(userId);
        showNotification(t('admin:orgDetail.userRestoreSuccess', { name: userName }), 'success');

        if (organizationId) {
          const updatedOrg = await organizationService.getOrganization(organizationId);
          setOrganization(updatedOrg);
        }
      } catch (error) {
        console.error('Error restoring user app access:', error);
        showNotification(t('admin:orgDetail.userRestoreError'), 'error');
      } finally {
        setRestoringUserId(null);
      }
    }
  };

  const handlePermanentDeleteUser = async (userId: string, userName: string) => {
    if (!organization) return;

    const confirmMessage = t('admin:orgDetail.userDeleteConfirm', { name: userName });

    if (window.confirm(confirmMessage)) {
      try {
        setDeletingUserId(userId);
        await userManagementService.deleteUser(userId);
        showNotification(t('admin:orgDetail.userDeleteSuccess', { name: userName }), 'success');

        if (organizationId) {
          const updatedOrg = await organizationService.getOrganization(organizationId);
          setOrganization(updatedOrg);
        }
      } catch (error) {
        console.error('Error permanently deleting user:', error);
        showNotification(t('admin:orgDetail.userDeleteError'), 'error');
      } finally {
        setDeletingUserId(null);
      }
    }
  };


  if (!organization) {
    return (
      <Box sx={{ paddingTop: '6.25rem', paddingX: 4 }}>
      </Box>
    );
  }

  const isActive = organization.status === 'active';
  const isInactive = organization.status === 'inactive';

  return (
    <Box sx={{
      paddingTop: '6.25rem',
      paddingX: 4,
      minHeight: '100vh',
    }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h4" sx={{
              color: 'text.primary',
              fontWeight: 400,
              fontSize: '2rem'
            }}>
              {organization.name}
            </Typography>
            <UniversalBadge
              type="organization-status"
              value={organization.status || 'active'}
              data-testid="admin-org-detail-status-badge"
            />
          </Box>
          <Typography variant="body1" sx={{
            color: 'text.secondary',
            fontSize: '1rem'
          }}>
            {organization.description}
          </Typography>
        </Box>

        {/* Organization Actions Menu */}
        <Box>
          <IconButton
            onClick={(e) => setOrganizationMenuAnchor(e.currentTarget)}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' }
            }}
            data-testid="admin-org-detail-organization-menu-button"
          >
            <MoreVertIcon />
          </IconButton>

          <Menu
            anchorEl={organizationMenuAnchor}
            open={Boolean(organizationMenuAnchor)}
            onClose={() => setOrganizationMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                border: '1px solid rgb(0 0 0 / 0.05)'
              }
            }}
            data-testid="admin-org-detail-organization-menu"
          >
            {isActive && (
              <MenuItemComponent
                onClick={handleDeactivateOrganization}
                sx={{ color: '#f59e0b', fontSize: '0.875rem' }}
                data-testid="admin-org-detail-deactivate-organization-button"
              >
                Organisation deaktivieren
              </MenuItemComponent>
            )}

            {isInactive && (
              <MenuItemComponent
                onClick={handleActivateOrganization}
                sx={{ color: '#10b981', fontSize: '0.875rem' }}
                data-testid="admin-org-detail-activate-organization-button"
              >
                Organisation aktivieren
              </MenuItemComponent>
            )}

            {isInactive ? (
              <MenuItemComponent
                onClick={handleDeleteOrganization}
                sx={{ color: '#ef4444', fontSize: '0.875rem' }}
                data-testid="admin-org-detail-delete-organization-button"
              >
                Endgültig löschen
              </MenuItemComponent>
            ) : (
              <MenuItemComponent
                onClick={() => {
                  showNotification(t('admin:messages.mustDeactivateFirst'), 'warning');
                  setOrganizationMenuAnchor(null);
                }}
                sx={{ color: '#bdbdbd', fontSize: '0.875rem' }}
                data-testid="admin-org-detail-delete-disabled-hint-button"
              >
                Erst deaktivieren zum Löschen
              </MenuItemComponent>
            )}
          </Menu>
        </Box>
      </Box>

      {/* Erste Statistik-Reihe - CSS Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(3, 1fr)'
        },
        gap: 3,
        mb: 4
      }}>
        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#2563eb',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:orgDetail.users')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#2563eb',
              fontSize: '2rem'
            }}>
              {organization.users.length}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#dc2626',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:orgDetail.serviceProviders')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#dc2626',
              fontSize: '2rem'
            }}>
              {organization.serviceProviderCount || 0}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          bgcolor: 'background.paper',
          border: organization.maxServices && (organization.totalServices ?? 0) >= organization.maxServices
            ? '2px solid #ef4444' : '1px solid rgb(0 0 0 / 0.05)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#16a34a',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:orgDetail.totalServices')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h3" sx={{
                fontWeight: 600,
                color: organization.maxServices && (organization.totalServices ?? 0) >= organization.maxServices
                  ? '#ef4444' : '#16a34a',
                fontSize: '2rem'
              }}>
                {organization.totalServices ?? 0}
              </Typography>
              {organization.maxServices && (
                <Typography variant="body1" sx={{
                  color: 'text.secondary',
                  fontSize: '1rem'
                }}>
                  / {organization.maxServices}
                </Typography>
              )}
            </Box>
            {organization.maxServices && (organization.totalServices ?? 0) >= organization.maxServices && (
              <Typography variant="caption" sx={{
                color: '#ef4444',
                fontSize: '0.75rem',
                mt: 0.5,
                display: 'block'
              }}>
                Limit erreicht
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Zweite Statistik-Reihe - CSS Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(4, 1fr)'
        },
        gap: 3,
        mb: 4
      }}>
        {/* Abgeschlossene Analysen */}
        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#4caf50',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:orgDetail.completedAnalyses')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#4caf50',
              fontSize: '2rem'
            }}>
              {organization.completedAnalyses ?? organization.completedServices ?? 0}
            </Typography>
          </CardContent>
        </Card>

        {/* Wird analysiert */}
        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#ff9800',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:orgDetail.analyzing')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#ff9800',
              fontSize: '2rem'
            }}>
              {organization.analyzingAnalyses ?? 0}
            </Typography>
          </CardContent>
        </Card>

        {/* Wartende Analysen */}
        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#2196f3',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:orgDetail.pendingAnalyses')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#2196f3',
              fontSize: '2rem'
            }}>
              {organization.pendingAnalyses ?? 0}
            </Typography>
          </CardContent>
        </Card>

        {/* Offene Analysen */}
        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:orgDetail.openAnalyses')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: 'text.secondary',
              fontSize: '2rem'
            }}>
              {organization.openAnalyses ?? organization.openServices ?? 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Service Limits & QS Configuration - CSS Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          lg: 'repeat(3, 1fr)'
        },
        gap: 3,
        mb: 4
      }}>
        <ServiceLimitsCard
          organization={organization}
          onUpdate={handleServiceLimitsUpdate}
        />
        <QSConfigCard
          organization={organization}
          onUpdate={setOrganization}
        />
        <UseCasesConfigCard
          organization={organization}
          onUpdate={setOrganization}
        />
      </Box>

      <Card sx={{ boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)', borderRadius: '8px', bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid rgb(0 0 0 / 0.05)'
          }}>
            <Typography variant="h6">
              {t('admin:orgDetail.usersCount', { count: organization.users.length })}
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setIsInviteDialogOpen(true)}
              data-testid="admin-org-detail-invite-user-button"
              sx={{ textTransform: 'none' }}
            >
              {t('admin:orgDetail.inviteUser')}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell data-testid="admin-org-detail-users-table-header-name">{t('admin:table.name')}</TableCell>
                  <TableCell data-testid="admin-org-detail-users-table-header-email">{t('admin:table.email')}</TableCell>
                  <TableCell data-testid="admin-org-detail-users-table-header-role">{t('admin:table.role')}</TableCell>
                  <TableCell data-testid="admin-org-detail-users-table-header-status">{t('admin:table.status')}</TableCell>
                  <TableCell data-testid="admin-org-detail-users-table-header-actions">{t('admin:table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organization.users.map((user) => {
                  // Status-basierte Logik für Aktionen
                  const userStatus = (user as any).status || 'active';
                  const isUserActive = userStatus === 'active' || userStatus === 'pending';
                  const isUserInactive = userStatus === 'inactive';
                  const isAppAccessRemoved = userStatus === 'app_access_removed';

                  // Status-Badge wird direkt verwendet

                  return (
                    <TableRow key={user._id} hover data-testid={`admin-org-detail-user-row-${user._id}`}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <UniversalBadge type="user-role" value={user.role} data-testid={`admin-org-detail-user-${user._id}-role-badge`} />
                      </TableCell>
                      <TableCell>
                        <UniversalBadge type="user-status" value={userStatus} data-testid={`admin-org-detail-user-${user._id}-status-badge`} />
                      </TableCell>
                      <TableCell>
                        {/* Rolle-Bearbeitung entfernt - wird vom Backend verwaltet */}

                        {/* Restore-Button: Nur für Benutzer mit entferntem App-Zugriff */}
                        {isAppAccessRemoved && (
                          <IconButton
                            size="small"
                            color="success"
                            sx={{ mr: 1 }}
                            onClick={() => handleRestoreUserAppAccess(user._id, user.name)}
                            title={t('admin:orgDetail.restoreAccess')}
                            data-testid={`admin-org-user-${user._id}-restore-button`}
                            disabled={restoringUserId === user._id || deletingUserId === user._id}
                          >
                            {restoringUserId === user._id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <RestoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}

                        {/* Entfernen-Button: Nur für aktive Benutzer */}
                        {isUserActive && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUser(user._id, user.name)}
                            title={t('admin:orgDetail.removeFromApp')}
                            data-testid={`admin-org-user-${user._id}-remove-button`}
                            disabled={removingUserId === user._id}
                          >
                            {removingUserId === user._id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}

                        {/* Endgültig löschen-Button: Nur für Benutzer mit entferntem App-Zugriff */}
                        {isAppAccessRemoved && (
                          <IconButton
                            size="small"
                            color="error"
                            sx={{ ml: 1 }}
                            onClick={() => handlePermanentDeleteUser(user._id, user.name)}
                            title={t('admin:orgDetail.permanentlyDelete')}
                            data-testid={`admin-org-user-${user._id}-permanent-delete-button`}
                            disabled={deletingUserId === user._id || restoringUserId === user._id}
                          >
                            {deletingUserId === user._id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog zum Benutzer einladen */}
      <InviteUserDialog
        open={isInviteDialogOpen}
        organizationId={organizationId || ''}
        organizationName={organization?.name || 'Organisation'}
        onClose={() => setIsInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
        data-testid="admin-org-detail-invite-user-dialog"
      />

      {/* Rolle-Bearbeitung Dialog entfernt - Rollen werden automatisch vom Backend verwaltet */}
    </Box>
  );
};

export default OrganizationDetail; 